/**
 * /settings/email — SMTP/OAuth email configuration (R3 FIX M.4).
 * Per-user settings stored in users.email_settings.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { API } from "../lib/backend";

export default function EmailSettingsPage() {
  const [provider, setProvider] = useState("aruba");
  const [emailAddress, setEmailAddress] = useState("");
  const [smtpHost, setSmtpHost] = useState("smtps.aruba.it");
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpPassword, setSmtpPassword] = useState("");
  const [passwordConfigured, setPasswordConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/email-settings`, { withCredentials: true })
      .then((r) => {
        const s = r.data?.settings;
        const d = r.data?.aruba_defaults || {};
        if (s) {
          setProvider(s.provider || "aruba");
          setEmailAddress(s.email_address || "");
          setSmtpHost(s.smtp_host || d.smtp_host || "smtps.aruba.it");
          setSmtpPort(s.smtp_port || d.smtp_port || 465);
          setPasswordConfigured(s.password_configured || false);
        } else {
          setSmtpHost(d.smtp_host || "smtps.aruba.it");
          setSmtpPort(d.smtp_port || 465);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        provider,
        email_address: emailAddress,
        smtp_host: smtpHost,
        smtp_port: Number(smtpPort),
        smtp_use_ssl: true,
      };
      if (smtpPassword) payload.smtp_password = smtpPassword;
      const r = await axios.put(`${API}/auth/email-settings`, payload, {
        withCredentials: true,
      });
      toast.success("Impostazioni email salvate");
      setPasswordConfigured(
        r.data?.settings?.password_configured || passwordConfigured
      );
      setSmtpPassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const r = await axios.post(
        `${API}/auth/email-settings/test`,
        {},
        { withCredentials: true }
      );
      if (r.data?.ok) {
        toast.success("Connessione SMTP OK ✓");
      } else {
        toast.error(`Test fallito: ${r.data?.error || "errore"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore test");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-[720px] mx-auto p-8" data-testid="email-settings-page">
      <div className="flex items-center gap-2 mb-6">
        <Mail className="h-5 w-5" style={{ color: "var(--accent-primary)" }} />
        <h1
          className="text-[20px] font-semibold"
          style={{ color: "var(--text-primary)" }}
        >
          Impostazioni email
        </h1>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
        </div>
      ) : (
        <div className="space-y-5 bg-white p-6 rounded-lg" style={{ border: "1px solid var(--border-subtle)" }}>
          <div>
            <div
              className="text-[11px] uppercase tracking-wide mb-2"
              style={{ color: "var(--text-muted)" }}
            >
              Provider
            </div>
            <div className="flex gap-2" data-testid="provider-radios">
              {[
                ["aruba", "Aruba SMTP"],
                ["gmail_oauth", "Gmail OAuth"],
                ["ms365_oauth", "Microsoft 365"],
              ].map(([val, label]) => {
                const disabled = val !== "aruba";
                return (
                  <button
                    key={val}
                    type="button"
                    disabled={disabled}
                    onClick={() => setProvider(val)}
                    className="px-3 py-1.5 rounded text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background:
                        provider === val && !disabled
                          ? "var(--accent-primary)"
                          : "var(--bg-subtle)",
                      color:
                        provider === val && !disabled
                          ? "white"
                          : "var(--text-primary)",
                      border: "1px solid var(--border-subtle)",
                    }}
                    data-testid={`provider-${val}`}
                  >
                    {label}
                    {disabled && (
                      <span className="ml-2 text-[10px] italic opacity-70">
                        Presto disponibile
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <div
              className="text-[11px] uppercase tracking-wide mb-1"
              style={{ color: "var(--text-muted)" }}
            >
              Email aziendale
            </div>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="commerciale@hotcoldsrl.com"
              className="w-full h-9 px-3 rounded-md text-[13px]"
              style={{ border: "1px solid var(--border-subtle)" }}
              data-testid="email-address-input"
            />
          </label>

          <div className="grid grid-cols-[1fr_100px] gap-3">
            <label className="block">
              <div
                className="text-[11px] uppercase tracking-wide mb-1"
                style={{ color: "var(--text-muted)" }}
              >
                Server SMTP
              </div>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                disabled={provider === "aruba"}
                className="w-full h-9 px-3 rounded-md text-[13px] disabled:opacity-60"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid="smtp-host-input"
              />
            </label>
            <label className="block">
              <div
                className="text-[11px] uppercase tracking-wide mb-1"
                style={{ color: "var(--text-muted)" }}
              >
                Porta
              </div>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                disabled={provider === "aruba"}
                className="w-full h-9 px-3 rounded-md text-[13px] disabled:opacity-60"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid="smtp-port-input"
              />
            </label>
          </div>

          <label className="block">
            <div
              className="text-[11px] uppercase tracking-wide mb-1 flex items-center gap-2"
              style={{ color: "var(--text-muted)" }}
            >
              Password applicazione
              {passwordConfigured && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
                  style={{ background: "#DCFCE7", color: "#166534" }}
                >
                  <Check className="h-2.5 w-2.5" /> già configurata
                </span>
              )}
            </div>
            <input
              type="password"
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              placeholder={
                passwordConfigured
                  ? "Lascia vuoto per mantenere quella esistente"
                  : "Inserisci la password applicazione"
              }
              className="w-full h-9 px-3 rounded-md text-[13px] font-mono"
              style={{ border: "1px solid var(--border-subtle)" }}
              data-testid="smtp-password-input"
            />
            <div
              className="text-[11px] mt-1"
              style={{ color: "var(--text-muted)" }}
            >
              Crittografata server-side con Fernet AES-256.
            </div>
          </label>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="h-9 px-4 rounded-md text-[13px] font-medium text-white disabled:opacity-50"
              style={{ background: "var(--accent-primary)" }}
              data-testid="email-save-btn"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button
              type="button"
              onClick={testConn}
              disabled={testing || !passwordConfigured}
              className="h-9 px-4 rounded-md text-[13px] disabled:opacity-50"
              style={{
                border: "1px solid var(--border-subtle)",
                color: "var(--text-primary)",
              }}
              data-testid="email-test-btn"
              title={
                !passwordConfigured
                  ? "Salva prima le credenziali"
                  : "Login SMTP dry-run (no mail)"
              }
            >
              {testing ? "Test…" : "Testa connessione"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
